import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTokenAuth } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
 
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  Stack,
  IconButton,
  Button,
  Card,
  CardContent,
  useTheme,
  alpha,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Tabs,
  Tab,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogContent
} from '@mui/material';
import CreateDisputeForm from './disputes/CreateDisputeForm';
import { isDeliveryOrder } from '../utils/deliveryUtils';
import {
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Gavel as GavelIcon,
  Notes as NotesIcon,
  RateReview as RateReviewIcon,
  ErrorOutline as ErrorOutlineIcon,
  AccessTime as AccessTimeIcon,
  QrCode as QrCodeIcon,
  SwapHoriz as SwapHorizIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { formatCurrency } from '../constants/dashboardConstants';
import { RESERVATION_STATUS, PAYMENT_TYPES } from '../constants/statusCodes';
import MessagingInterface from '../components/MessagingInterface';
import { useMessageNotifications } from '../hooks/useMessageNotifications';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import QRCodeGeneratorModal from './QRCodeGeneratorModal';
import QRCodeConfirmationModal from './QRCodeConfirmationModal';
import QRCodeService from '../services/qrCodeService';
import ConditionReportsSection from './ConditionReportsSection';
import DeliveryOrPickupSection from './delivery/DeliveryOrPickupSection';
import { toast } from 'react-toastify';
import { translateQRError, getQRSuccessMessage, getQRTechnicalError } from '../utils/qrTranslations';
import OwnerCreatedReview from './reviews/OwnerCreatedReview';
import OwnerReviewDisplay from './reviews/OwnerReviewDisplay';
import OwnerReviewEmpty from './reviews/OwnerReviewEmpty';
import api from '../services/api';
import DisputeSummaryCard from './financial/DisputeSummaryCard';
import RideShareMap from './listing/RideShareMap';
import ReadOnlyMap from './common/ReadOnlyMap';

const ReservationDetailsPage = ({ 
  reservation, 
  currentUser, 
  onBack, // onBack(hasDataChanged) - callback avec info de modification
  onDataUpdate, // Callback pour le rafraîchissement des données parent
  onReservationRefresh, // Callback pour recharger la réservation spécifique
  userRole = 'renter', // 'renter' ou 'owner'
  scrollToTracking = false // Nouvelle prop pour scroll automatique vers tracking
}) => {
  const theme = useTheme();
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  // État pour tracker si des modifications ont été faites
  const [hasDataChanged, setHasDataChanged] = useState(false);
  
  // État pour les onglets sur mobile
  const [mobileTab, setMobileTab] = useState(0);
  
  // États pour les modales QR
  const [qrGeneratorOpen, setQrGeneratorOpen] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrType, setQrType] = useState(''); // 'pickup' ou 'return'
  const [qrLoading, setQrLoading] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  
  // État pour le loading de confirmation d'expédition
  const [shipmentLoading, setShipmentLoading] = useState(false);
  
  // Hook pour les notifications de messages
  const { getUnreadCount } = useMessageNotifications();

  const [reservationReview, setReservationReview] = useState(reservation?.reservationReview || null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewFetchAttempted, setReviewFetchAttempted] = useState(false);

  useEffect(() => {
    setReservationReview(reservation?.reservationReview || null);
    setReviewFetchAttempted(false);
  }, [reservation?.id, reservation?.reservationReview]);

  useEffect(() => {
    const shouldFetch =
      reservation?.id &&
      reservation?.status === RESERVATION_STATUS.COMPLETED &&
      !reservation?.reservationReview &&
      !reviewFetchAttempted;

    if (!shouldFetch) {
      return;
    }

    const fetchReservationReview = async () => {
      try {
        setReviewLoading(true);
        const response = await api.get(`/reviews/reservations/${reservation.id}`);
        const reviewData = response?.data?.data ?? response?.data;
        setReservationReview(reviewData || null);
      } catch (err) {
        if (err.response?.status === 404) {
          setReservationReview(null);
        } else {
          console.error('Error fetching reservation review for reservation details:', err);
        }
      } finally {
        setReviewLoading(false);
        setReviewFetchAttempted(true);
      }
    };

    fetchReservationReview();
  }, [reservation?.id, reservation?.status, reservation?.reservationReview, reviewFetchAttempted]);

  const reservationDataForDisplay = useMemo(() => {
    if (!reservation) {
      return reservation;
    }
    if (reservation?.reservationReview) {
      return reservation;
    }
    if (!reservationReview) {
      return reservation;
    }
    return {
      ...reservation,
      reservationReview
    };
  }, [reservation, reservationReview]);

  // Optimiser les vérifications QR avec useMemo pour éviter les re-calculs
  const qrCapabilities = useMemo(() => {
    if (!reservation || !currentUser?.id) {
      return {
        canGeneratePickupQR: false,
        canScanPickupQR: false,
        canGenerateReturnQR: false,
        canScanReturnQR: false
      };
    }
    
    return {
      canGeneratePickupQR: QRCodeService.canGenerateQRCode(reservation, 'pickup', currentUser.id),
      canScanPickupQR: QRCodeService.canScanQRCode(reservation, 'pickup', currentUser.id),
      canGenerateReturnQR: QRCodeService.canGenerateQRCode(reservation, 'return', currentUser.id),
      canScanReturnQR: QRCodeService.canScanQRCode(reservation, 'return', currentUser.id)
    };
  }, [reservation?.id, reservation?.status, reservation?.pickup_confirmed, reservation?.return_confirmed, currentUser?.id]);

  // Extraire les capacités du cache
  const { canGeneratePickupQR, canScanPickupQR, canGenerateReturnQR, canScanReturnQR } = qrCapabilities;  
  const normalizePaymentData = (payment) => {
    return {
      ...payment,
      payment_type: payment.payment_type || payment.type || PAYMENT_TYPES.RENTAL,
      payment_method: payment.payment_method || payment.method || 'card',
      transaction_date: payment.created_at || payment.transaction_date || null
    };
  };

  // Calculs dérivés sécurisés (avant early return)
  // S'assurer que payments est toujours un tableau
  const paymentsRaw = reservation?.payment_history || reservation?.payments || [];
  const payments = Array.isArray(paymentsRaw) ? paymentsRaw : (paymentsRaw.map ? paymentsRaw.map(normalizePaymentData) : []);
  
  // Filtrer les paiements pour exclure les types internes
  const visiblePayments = useMemo(() => {
    return Array.isArray(payments) ? payments.filter(payment => (payment.type || payment.payment_type) !== PAYMENT_TYPES.DEPOSIT_REFUND) : [];
  }, [payments]);
  
  // Calculer le statut du dépôt à partir des paiements réels
  const depositPayment = useMemo(() => {
    if (!Array.isArray(payments) || payments.length === 0) return null;
    return payments.find(
      (p) => (p.type || p.payment_type) === PAYMENT_TYPES.DEPOSIT
    );
  }, [payments]);

  if (!reservation) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('reservationDetails.messages.reservationNotFound')}
        </Typography>
      </Box>
    );
  }

  // Fonction pour obtenir les données du listing selon le format
  const getListingData = () => {
    const listing = reservation.listing || reservation.Listing || {};
    return listing;
  };

  // Fonction pour obtenir les données de l'autre participant
  const getOtherParticipant = () => {
    const listing = getListingData();
    if (userRole === 'renter') {
      // Si l'utilisateur actuel est le locataire, récupérer le propriétaire
      return listing.User || listing.owner || listing.Owner || null;
    } else {
      // Si l'utilisateur actuel est le propriétaire, récupérer le locataire
      return reservation.renter || reservation.User || null;
    }
  };

  const getStatusConfig = (status) => {
    const statusConfig = {
      [RESERVATION_STATUS.PENDING]: {
        color: 'warning',
        icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
        label: t('reservationDetails.status.pending')
      },
      [RESERVATION_STATUS.CONFIRMED]: {
        color: 'success',
        icon: <CheckIcon sx={{ fontSize: 16 }} />,
        label: t('reservationDetails.status.confirmed')
      },
      [RESERVATION_STATUS.ACTIVE]: {
        color: 'info',
        icon: <EventIcon sx={{ fontSize: 16 }} />,
        label: t('reservationDetails.status.active')
      },
      [RESERVATION_STATUS.COMPLETED]: {
        color: 'primary',
        icon: <CheckIcon sx={{ fontSize: 16 }} />,
        label: t('reservationDetails.status.completed')
      },
      [RESERVATION_STATUS.CANCELLED]: {
        color: 'error',
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        label: t('reservationDetails.status.cancelled')
      }
    };
    
    return statusConfig[status] || statusConfig[RESERVATION_STATUS.PENDING];
  };

  // Fonctions utilitaires pour formater les données
  const formatDate = (date) => {
    if (!date) return t('common.notDefined');
    return format(new Date(date), 'dd/MM/yyyy HH:mm');
  };

  const formatDateOnly = (date) => {
    if (!date) return t('common.notDefined');
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const formatTime = (date) => {
    if (!date) return t('common.notDefined');
    return format(new Date(date), 'HH:mm');
  };

  const getPaymentStatusConfig = (status) => {
    const configs = {
      'pending': { color: 'warning', label: t('reservationDetails.paymentStatus.pending'), icon: <ScheduleIcon /> },
      'completed': { color: 'success', label: t('reservationDetails.paymentStatus.completed'), icon: <CheckIcon /> },
      'partial': { color: 'info', label: t('reservationDetails.paymentStatus.partial'), icon: <InfoIcon /> },
      'refunded': { color: 'error', label: t('reservationDetails.paymentStatus.refunded'), icon: <RefreshIcon /> },
      'failed': { color: 'error', label: t('reservationDetails.paymentStatus.failed'), icon: <ErrorOutlineIcon /> },
      'cancelled': { color: 'default', label: t('reservationDetails.paymentStatus.cancelled'), icon: <CancelIcon /> }
    };
    return configs[status] || configs['pending'];
  };

  const getDepositStatusConfig = (status) => {
    const configs = {
      'pending': { color: 'warning', label: t('reservationDetails.depositStatus.pending'), icon: <ScheduleIcon /> },
      'paid': { color: 'success', label: t('reservationDetails.depositStatus.paid'), icon: <CheckIcon /> },
      'refunded': { color: 'info', label: t('reservationDetails.depositStatus.refunded'), icon: <RefreshIcon /> },
      'withheld': { color: 'error', label: t('reservationDetails.depositStatus.withheld'), icon: <WarningIcon /> },
      'held': { color: 'primary', label: t('reservationDetails.depositStatus.held'), icon: <SecurityIcon /> },
      'partially_captured': { color: 'warning', label: t('reservationDetails.depositStatus.partially_captured'), icon: <SwapHorizIcon /> },
      'released': { color: 'success', label: t('reservationDetails.depositStatus.released'), icon: <CheckCircleOutlineIcon /> },
      'cancelled': { color: 'default', label: t('reservationDetails.depositStatus.cancelled'), icon: <CancelIcon /> }
    };
    return configs[status] || configs['pending'];
  };
  const listing = getListingData();
  const otherParticipant = getOtherParticipant();
  const statusConfig = getStatusConfig(reservation.status);
  const currency = reservation.currency || listing.currency || 'EUR';
  
  // Fonction pour traduire le type de paiement
  const getPaymentTypeLabel = (paymentType) => {
    const types = {
      [PAYMENT_TYPES.RENTAL]: t('reservationDetails.paymentTypes.rental'),
      [PAYMENT_TYPES.COMMISSION]: t('reservationDetails.paymentTypes.commission'),
      [PAYMENT_TYPES.DEPOSIT]: t('reservationDetails.paymentTypes.deposit'),
      [PAYMENT_TYPES.TAX]: t('reservationDetails.paymentTypes.tax'),
      [PAYMENT_TYPES.REFUND]: t('reservationDetails.paymentTypes.refund'),
      'damage_compensation': t('reservationDetails.paymentTypes.damage_compensation'),
      'dispute_refund': t('reservationDetails.paymentTypes.dispute_refund')
    };
    return types[paymentType] || paymentType;
  };

  // Fonction pour traduire la méthode de paiement
  const getPaymentMethodLabel = (paymentMethod) => {
    const methods = {
      'card': t('reservationDetails.paymentMethods.card'),
      'stripe': t('reservationDetails.paymentMethods.card'),
      'cash': t('reservationDetails.paymentMethods.cash'),
      'virement': t('reservationDetails.paymentMethods.transfer'),
      'automatic_credit': t('reservationDetails.paymentMethods.automaticCredit')
    };
    return methods[paymentMethod] || paymentMethod;
  };
  
  // Extraction des données enrichies des modèles avec priorisation payment_history
  // Le backend renvoie deux sources de paiements:
  // 1. reservation.payments (données brutes, peuvent avoir des null)
  // 2. reservation.payment_history (données formatées correctement - ARRAY direct)
  
  const deposits = reservation.deposits || [];
  const codes = reservation.codes || [];
  const depositAmountValue = reservation.deposit_amount != null ? parseFloat(reservation.deposit_amount) : null;
  const hasDepositAmount = Number.isFinite(depositAmountValue) && depositAmountValue > 0;
  
  // Calculs dérivés
  const start = new Date(reservation.start_date);
  const end = new Date(reservation.end_date);

  // Normalisation pour éviter les soucis d’heure/ fuseau
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));

  // Durée affichée : utilise start_at/end_at (précis, en heures/min) quand disponibles
  // sinon retombe sur totalDays (en jours) pour les réservations date_range
  const durationLabel = (() => {
    if (reservation.start_at && reservation.end_at) {
      const diffMs = new Date(reservation.end_at) - new Date(reservation.start_at);
      const totalMinutes = Math.round(diffMs / 60000);
      if (totalMinutes <= 0) return '1 créneau';
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours === 0) return `${minutes} min`;
      if (minutes === 0) return `${hours} h`;
      return `${hours} h ${minutes} min`;
    }
    return totalDays === 1 ? '1 jour' : `${totalDays} jours`;
  })();

  const hasExtraFees = reservation.extra_fees && Object.keys(reservation.extra_fees).length > 0;
  const hasDamages = reservation.damage_amount && parseFloat(reservation.damage_amount) > 0;
  const isRated = (userRole === 'renter' && reservation.renter_rating) || (userRole === 'owner' && reservation.owner_rating);

  // Fonctions de gestion QR Code
  const handleGenerateQR = async (reservationId, type) => {
    setQrLoading(true);
    try {
      const result = await QRCodeService.generateQRCode(reservationId, type);
      return result;
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error('Erreur lors de la génération du QR code');
      return { success: false, error: 'Erreur technique' };
    } finally {
      setQrLoading(false);
    }
  };

  const handleConfirmation = async (confirmationData) => {
    setQrLoading(true);
    try {
      let result;
      if (qrType === 'pickup') {
        result = await QRCodeService.confirmPickup(reservation.id, confirmationData);
      } else {
        result = await QRCodeService.confirmReturn(reservation.id, confirmationData);
      }
      
      if (result.success) {
        toast.success(getQRSuccessMessage(qrType, t));
        setQrScannerOpen(false);
        // Marquer qu'une modification a été effectuée
        console.log('✅ Confirmation QR réussie, marquage hasDataChanged = true');
        setHasDataChanged(true);
        
        // Rafraîchir d'abord les données parent pour mettre à jour la liste
        if (onDataUpdate) {
          console.log('🔄 Rafraîchissement données parent...');
          await onDataUpdate();
        }
        
        // Ensuite rafraîchir la réservation spécifique dans le modal
        if (onReservationRefresh) {
          console.log('🔄 Rafraîchissement réservation spécifique...');
          await onReservationRefresh(reservation.id);
        }
        
        console.log('✅ Tous les rafraîchissements terminés');
      } else {
        toast.error(translateQRError(result, t), {
          autoClose: 8000, // 8 secondes pour que l'utilisateur ait le temps de lire
          position: "top-center" // Position plus visible
        });
        
        // NE PAS fermer le modal pour les erreurs de validation
        // L'utilisateur peut réessayer avec un autre code
      }
    } catch (error) {
      console.error('Error confirming:', error);
      toast.error(getQRTechnicalError(t), {
        autoClose: 8000,
        position: "top-center"
      });
    } finally {
      setQrLoading(false);
    }
  };

  const openQRGenerator = (type) => {
    setQrType(type);
    setQrGeneratorOpen(true);
  };

  const openQRScanner = (type) => {
    setQrType(type);
    setQrScannerOpen(true);
  };

  // Fonction pour gérer la fermeture du modal avec information des modifications
  const handleClose = () => {
    console.log('🚪 Fermeture du modal de détails, hasDataChanged:', hasDataChanged);
    if (onBack) {
      onBack(hasDataChanged);
    }
  };

  // Fonction pour télécharger le contrat de location
  const handleDownloadContract = async () => {
    try {
  

      const token = getTokenAuth();     

      if (!token) {
        console.error('❌ Aucun token d\'authentification trouvé');
        toast.error('Vous devez être connecté pour télécharger le contrat');
        return;
      }

      console.log('📞 Appel API pour récupérer les documents...');
      // Étape 1: Récupérer les documents de la réservation
      const response = await fetch(`/api/v1/documents/reservation/${reservation.id}?type=contract`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📞 Réponse API:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erreur API:', errorText);
        throw new Error('Erreur lors de la récupération du document');
      }

      const documents = await response.json();
      console.log('📄 Documents reçus:', documents);
      
      if (!documents || !documents.data || documents.data.length === 0) {
        toast.error(t('reservationDetails.messages.contractNotFound'));
        return;
      }

      const contractDocument = documents.data[0]; // Premier document contrat trouvé
      console.log('📄 Document contrat:', contractDocument);

      // Étape 2: Télécharger le document via l'endpoint existant
      console.log('⬇️ Téléchargement du document...');
      const downloadResponse = await fetch(`/api/v1/documents/${contractDocument.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text();
        console.error('❌ Erreur téléchargement:', errorText);
        throw new Error('Erreur lors du téléchargement');
      }

      // Créer un blob et télécharger le fichier
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat_reservation_${reservation.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('reservationDetails.messages.contractDownloaded'));
    } catch (error) {
      console.error('Erreur téléchargement contrat:', error);
      toast.error(t('reservationDetails.messages.downloadError'));
    }
  };

  // Fonction pour normaliser les données de paiement
  const shouldShowPickupCode = () => {
    // Le code pickup (client_code) est masqué pour le owner jusqu'à validation
    if (userRole === 'owner') {
      return reservation.pickup_confirmed;
    }
    // Pour le renter, toujours visible (c'est son code)
    return true;
  };

  const shouldShowReturnCode = () => {
    // Le code return (owner_code) est masqué pour le renter jusqu'à validation  
    if (userRole === 'renter') {
      return reservation.return_confirmed;
    }
    // Pour l'owner, toujours visible (c'est son code)
    return true;
  };

  const isPickupCodeVisible = shouldShowPickupCode();
  const isReturnCodeVisible = shouldShowReturnCode();

  return (
    <Box sx={{ 
      height: '100vh',
      maxHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Overlay de chargement pendant la confirmation d'expédition */}
      {shipmentLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            {t('delivery:confirmingShipment', 'Confirmation en cours...')}
          </Typography>
        </Box>
      )}
      
      {/* En-tête avec informations principales */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          p: 3,
          backgroundColor: 'white',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <IconButton onClick={handleClose} sx={{ color: theme.palette.primary.main }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="600" color="primary.main">
            {t('reservationDetails.title')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
            <Chip 
              {...statusConfig}
              variant="filled"
              size="small"
              icon={statusConfig.icon}
            />
            {(Array.isArray(reservation.disputes) && reservation.disputes.length > 0) || reservation.dispute ? (
              <Chip
                label={`${(Array.isArray(reservation.disputes) ? reservation.disputes.length : 1)} ${t('reservationDetails.labels.dispute')}`}
                size="small"
                color="warning"
                variant="filled"
                icon={<GavelIcon />}
                sx={{ color: 'white', fontWeight: 'bold' }}
              />
            ) : null}
            {Array.isArray(reservation.penalties) && reservation.penalties.length > 0 ? (
              <Chip
                label={`${reservation.penalties.length} ${t('reservationDetails.labels.penalty')}`}
                size="small"
                color="error"
                variant="filled"
                icon={<GavelIcon />}
                sx={{ color: 'white', fontWeight: 'bold' }}
              />
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      {/* Onglets mobiles - seulement visible sur petit écran */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, borderBottom: 1, borderColor: 'divider', backgroundColor: 'white' }}>
        <Tabs 
          value={mobileTab} 
          onChange={(event, newValue) => setMobileTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            icon={<ReceiptIcon />} 
            label={t('reservationDetails.mobileTabs.details', 'Détails')} 
          />
          {(reservation.status !== RESERVATION_STATUS.CANCELLED && 
            reservation.status !== RESERVATION_STATUS.EXPIRED) && (
            <Tab 
              icon={<Badge badgeContent={getUnreadCount(reservation.id)} color="error"><ChatIcon /></Badge>} 
              label={t('reservationDetails.mobileTabs.messages', 'Messages')} 
            />
          )}
        </Tabs>
      </Box>

      <Box sx={{ 
          flex: 1,
          display: { xs: 'flex', md: 'grid' },
          flexDirection: { xs: 'column', md: 'initial' },
          gridTemplateColumns: { 
            md: (reservation.status === RESERVATION_STATUS.CANCELLED || 
                 reservation.status === RESERVATION_STATUS.EXPIRED) ? '1fr' : '1.2fr 0.8fr' 
          },
          gap: 0,
          overflow: 'hidden',
          minHeight: 0
        }}>
          {/* Panneau gauche - Détails enrichis avec accordéons */}
          <Box 
            sx={{ 
              overflowY: 'auto',
              backgroundColor: 'white',
              position: 'relative',
              display: { xs: mobileTab === 0 ? 'block' : 'none', md: 'block' },
              flex: { xs: 1, md: 'initial' },
              height: { xs: '100%', md: 'auto' },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                }
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              {/* Section 1: Informations du listing */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">{t('reservationDetails.sections.equipmentInfo')}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={listing.primary_image || listing.mainImageUrl || listing.product?.images?.[0] || listing.images?.[0]?.image_url || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%23ddd" width="400" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImage non disponible%3C/text%3E%3C/svg%3E'}
                        alt={listing.title || 'Matériel'}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          // Éviter la boucle infinie en vérifiant si on a déjà mis le placeholder
                          if (!e.target.dataset.errorHandled) {
                            e.target.dataset.errorHandled = 'true';
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%23ddd" width="400" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImage non disponible%3C/text%3E%3C/svg%3E';
                          }
                        }}
                      />
                    </Box>
                    
                    <CardContent>
                      <Typography variant="h5" fontWeight="700" gutterBottom>
                        {listing.title || t('reservationDetails.messages.equipmentNotAvailable')}
                      </Typography>
                      
                      {listing.location && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                          <LocationIcon color="primary" />
                          <Typography variant="body1">{listing.location}</Typography>
                        </Stack>
                      )}

                      {listing.description && (
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: showFullDescription ? 'none' : 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.6
                            }}
                          >
                            {listing.description}
                          </Typography>
                          {listing.description.length > 150 && (
                            <Button
                              size="small"
                              onClick={() => setShowFullDescription(!showFullDescription)}
                              sx={{ mt: 1, p: 0, textTransform: 'none' }}
                            >
                              {showFullDescription ? t('common.seeLess') : t('common.seeMore')}
                            </Button>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </AccordionDetails>
              </Accordion>

              {/* Section 2: Informations de la réservation */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">{t('reservationDetails.sections.reservationDetails')}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Métadonnées principales */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.reservationId')}</Typography>
                        <Typography variant="body2" fontWeight="600" color="primary.main" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {reservation.id || 'N/A'}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                        <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.createdOn')}</Typography>
                        <Typography variant="body1" fontWeight="500" color="info.main">
                          {formatDateOnly(reservation.created_at)}
                        </Typography>
                      </Card>
                    </Grid>
                    {!isDeliveryOrder(reservation) && (
                      <Grid item xs={12} md={6}>
                        <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                          <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.duration')}</Typography>
                          <Typography variant="h6" fontWeight="600" color="warning.main">
                            {durationLabel}
                          </Typography>
                        </Card>
                            {/* Quantité / Capacité */}
                          {(reservation.quantity_requested > 1 || reservation.seats_requested > 1) && (
                            <>
                              {reservation.quantity_requested > 1 && (
                              <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                                    <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.quantityRequested')}</Typography>
                                    <Typography variant="h6" fontWeight="600" color="secondary.main">
                                      {reservation.quantity_requested}
                                      {listing.stock_quantity && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                          / {listing.stock_quantity} ({t('reservationDetails.labels.stockQuantity')})
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Card>
                              )}
                              {reservation.seats_requested > 1 && (
                              <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                                    <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.seatsRequested')}</Typography>
                                    <Typography variant="h6" fontWeight="600" color="info.main">
                                      {reservation.seats_requested}
                                      {listing.capacity_default && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                          / {listing.capacity_default} ({t('reservationDetails.labels.capacityDefault')})
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Card>
                              )}
                            </>
                          )}                      
                      </Grid>
                    )}                    
                    <Grid item xs={12} md={6}>
                      <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                        <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.totalAmount')}</Typography>
                        <Typography variant="h6" fontWeight="600" color="success.main">
                          {formatCurrency(parseFloat(reservation.total_amount || 0), currency)}
                        </Typography>
                      </Card>
                    </Grid>
                    {!isDeliveryOrder(reservation) && (
                      <Grid item xs={12} md={6}>
                        <Card elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>{t('reservationDetails.labels.start')}</Typography>
                          <Typography variant="h6" fontWeight="600" color="primary.main">
                            {formatDateOnly(reservation.start_date)}
                          </Typography>
                          {reservation.start_at && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                             {formatTime(reservation.start_at)}
                            </Typography>
                          )}
                          {reservation.pickup_time && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {t('reservationDetails.labels.appointmentTime')}: {reservation.pickup_time}
                            </Typography>
                          )}
                          {reservation.pickup_address && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                              {reservation.pickup_address}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Chip 
                              size="small" 
                              icon={
                                reservation.status === RESERVATION_STATUS.CANCELLED ? <CancelIcon /> :
                                reservation.pickup_confirmed ? <CheckIcon /> : <ScheduleIcon />
                              }
                              label={
                                reservation.status === RESERVATION_STATUS.CANCELLED ? t('reservationDetails.status.pickupCancelled') :
                                reservation.pickup_confirmed ? t('reservationDetails.status.pickedUp') : t('reservationDetails.status.waitingPickup')
                              }
                              color={
                                reservation.status === RESERVATION_STATUS.CANCELLED ? 'error' :
                                reservation.pickup_confirmed ? 'success' : 'warning'
                              }
                            />
                          </Box>
                        </Card>
                      </Grid>
                    )}
                    
                    {!isDeliveryOrder(reservation) && (
                      <Grid item xs={12} md={6}>
                        <Card elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>{t('reservationDetails.labels.end')}</Typography>
                          <Typography variant="h6" fontWeight="600" color="secondary.main">
                            {formatDateOnly(reservation.end_date)}
                          </Typography>
                          {reservation.end_at && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {formatTime(reservation.end_at)}
                            </Typography>
                          )}
                          {reservation.return_time && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {t('reservationDetails.labels.appointmentTime')}: {reservation.return_time}
                            </Typography>
                          )}
                          {reservation.return_address && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                              {reservation.return_address}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Chip 
                              size="small" 
                              icon={
                                reservation.status === RESERVATION_STATUS.CANCELLED ? <CancelIcon /> :
                                reservation.return_confirmed ? <CheckIcon /> : <ScheduleIcon />
                              }
                              label={
                                reservation.status === RESERVATION_STATUS.CANCELLED ? t('reservationDetails.status.returnCancelled') :
                                reservation.return_confirmed ? t('reservationDetails.status.returned') : t('reservationDetails.status.waitingReturn')
                              }
                              color={
                                reservation.status === RESERVATION_STATUS.CANCELLED ? 'error' :
                                reservation.return_confirmed ? 'success' : 'warning'
                              }
                            />
                          </Box>
                        </Card>
                      </Grid>
                    )}

                    {/* Codes d'accès et QR Codes / Livraison */}
                    <Grid item xs={12}>
                      <DeliveryOrPickupSection
                        reservation={reservation}
                        currentUser={currentUser}
                        userRole={userRole}
                        scrollToTracking={scrollToTracking}
                        onStartLoading={() => setShipmentLoading(true)}
                        onError={(errorMessage) => {
                          console.log('❌ [ReservationDetailsPage] Erreur d\'expédition:', errorMessage);
                          // Désactiver le loading
                          setShipmentLoading(false);
                          // Afficher le message d'erreur via toast si disponible
                          if (toast && toast.error) {
                            toast.error(errorMessage);
                          }
                        }}
                        onReservationUpdate={async (updatedReservation) => {
                          console.log('📦 [ReservationDetailsPage] onReservationUpdate appelé');
                          console.log('📦 [ReservationDetailsPage] updatedReservation:', updatedReservation);
                          console.log('📦 [ReservationDetailsPage] owner_notes:', updatedReservation?.owner_notes);
                          
                          // Mise à jour locale de la réservation dans le modal avec TOUTES les données du backend
                          Object.assign(reservation, {
                            ...updatedReservation,
                            pickup_confirmed: updatedReservation?.pickup_confirmed ?? true,
                            return_confirmed: updatedReservation?.return_confirmed ?? true,
                            status: updatedReservation?.status ?? 'completed'
                          });
                          
                          console.log('📦 [ReservationDetailsPage] Réservation après assign:', reservation);
                          console.log('📦 [ReservationDetailsPage] reservation.owner_notes:', reservation.owner_notes);
                          
                          // Marquer qu'une modification a été effectuée
                          setHasDataChanged(true);
                          
                          // Désactiver le loading
                          setShipmentLoading(false);
                          
                          // Passer TOUTES les données mises à jour au parent pour mettre à jour le card
                          if (onDataUpdate) {
                            await onDataUpdate({
                              ...updatedReservation,
                              id: reservation.id,
                              pickup_confirmed: updatedReservation?.pickup_confirmed ?? true,
                              return_confirmed: updatedReservation?.return_confirmed ?? true,
                              status: updatedReservation?.status ?? 'completed'
                            });
                          }
                        }}
                        renderPickupReturnSection={() => (
                          <Card elevation={1} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                              <QrCodeIcon color="primary" />
                              {t('reservationDetails.sections.pickupReturn')}
                            </Typography>
                            
                            {/* Section Pickup */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                📦 {t('reservationDetails.labels.pickup')}
                              </Typography>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={6}>
                                  <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                                    <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.clientCode')}</Typography>
                                    {isPickupCodeVisible ? (
                                      <Typography variant="h5" fontWeight="700" color="info.main">
                                        {reservation.client_code || t('reservationDetails.messages.notGenerated')}
                                      </Typography>
                                    ) : (
                                      <Typography variant="h5" fontWeight="700" color="text.secondary">
                                        🔒 ••••••
                                      </Typography>
                                    )}
                                    <Chip 
                                      size="small" 
                                      icon={
                                        reservation.status === RESERVATION_STATUS.CANCELLED ? <CancelIcon /> :
                                        reservation.pickup_confirmed ? <CheckIcon /> : <ScheduleIcon />
                                      }
                                      label={
                                        reservation.status === RESERVATION_STATUS.CANCELLED ? t('reservationDetails.status.pickupCancelled') :
                                        reservation.pickup_confirmed ? t('reservationDetails.status.pickupConfirmed') : t('reservationDetails.status.pending')
                                      }
                                      color={
                                        reservation.status === RESERVATION_STATUS.CANCELLED ? 'error' :
                                        reservation.pickup_confirmed ? 'success' : 'warning'
                                      }
                                      sx={{ mt: 1 }}
                                    />
                                    {!isPickupCodeVisible && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        {t('reservationDetails.messages.codeMasked')}
                                      </Typography>
                                    )}
                                  </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Stack spacing={1}>
                                    {/* RENTER: peut seulement GÉNÉRER le QR Pickup */}
                                    {userRole === 'renter' && canGeneratePickupQR && (
                                      <Button
                                        variant="contained"
                                        startIcon={<QrCodeIcon />}
                                        onClick={() => openQRGenerator('pickup')}
                                        disabled={qrLoading}
                                        fullWidth
                                        color="primary"
                                      >
                                        {t('reservationDetails.actions.generatePickupQR')}
                                      </Button>
                                    )}
                                    {/* OWNER: peut seulement SCANNER le QR Pickup */}
                                    {userRole === 'owner' && canScanPickupQR && (
                                      <Button
                                        variant="outlined"
                                        startIcon={<QrCodeScannerIcon />}
                                        onClick={() => openQRScanner('pickup')}
                                        disabled={qrLoading}
                                        fullWidth
                                        color="primary"
                                      >
                                        {t('reservationDetails.actions.scanPickupQR')}
                                      </Button>
                                    )}
                                    {!canGeneratePickupQR && !canScanPickupQR && reservation.pickup_confirmed && (
                                      <Button
                                        variant="outlined"
                                        startIcon={<CheckIcon />}
                                        disabled
                                        fullWidth
                                        color="success"
                                      >
                                        {t('reservationDetails.status.pickupConfirmed')}
                                      </Button>
                                    )}
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Section Return */}
                            <Box>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                🔄 {t('reservationDetails.labels.return')}
                              </Typography>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={6}>
                                  <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
                                    <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.ownerCode')}</Typography>
                                    {isReturnCodeVisible ? (
                                      <Typography variant="h5" fontWeight="700" color="warning.main">
                                        {reservation.owner_code || t('reservationDetails.messages.notGenerated')}
                                      </Typography>
                                    ) : (
                                      <Typography variant="h5" fontWeight="700" color="text.secondary">
                                        🔒 ••••••
                                      </Typography>
                                    )}
                                    <Chip 
                                      size="small" 
                                      icon={
                                        reservation.status === RESERVATION_STATUS.CANCELLED ? <CancelIcon /> :
                                        reservation.return_confirmed ? <CheckIcon /> : <ScheduleIcon />
                                      }
                                      label={
                                        reservation.status === RESERVATION_STATUS.CANCELLED ? t('reservationDetails.status.returnCancelled') :
                                        reservation.return_confirmed ? t('reservationDetails.status.returnConfirmed') : t('reservationDetails.status.pending')
                                      }
                                      color={
                                        reservation.status === RESERVATION_STATUS.CANCELLED ? 'error' :
                                        reservation.return_confirmed ? 'success' : 'warning'
                                      }
                                      sx={{ mt: 1 }}
                                    />
                                    {!isReturnCodeVisible && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        {t('reservationDetails.messages.codeMasked')}
                                      </Typography>
                                    )}
                                  </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Stack spacing={1}>
                                    {/* OWNER: peut seulement GÉNÉRER le QR Return */}
                                    {userRole === 'owner' && canGenerateReturnQR && (
                                      <Button
                                        variant="contained"
                                        startIcon={<QrCodeIcon />}
                                        onClick={() => openQRGenerator('return')}
                                        disabled={qrLoading || !reservation.pickup_confirmed}
                                        fullWidth
                                        color="secondary"
                                      >
                                        {t('reservationDetails.actions.generateReturnQR')}
                                      </Button>
                                    )}
                                    {/* RENTER: peut seulement SCANNER le QR Return */}
                                    {userRole === 'renter' && canScanReturnQR && (
                                      <Button
                                        variant="outlined"
                                        startIcon={<QrCodeScannerIcon />}
                                        onClick={() => openQRScanner('return')}
                                        disabled={qrLoading || !reservation.pickup_confirmed}
                                        fullWidth
                                        color="secondary"
                                      >
                                        {t('reservationDetails.actions.scanReturnQR')}
                                      </Button>
                                    )}
                                    {!canGenerateReturnQR && !canScanReturnQR && reservation.return_confirmed && (
                                      <Button
                                        variant="outlined"
                                        startIcon={<CheckIcon />}
                                        disabled
                                        fullWidth
                                        color="success"
                                      >
                                        {t('reservationDetails.status.returnConfirmed')}
                                      </Button>
                                    )}
                                    {!reservation.pickup_confirmed && (
                                      <Typography variant="caption" color="text.secondary" textAlign="center">
                                        {t('reservationDetails.messages.pickupRequiredBeforeReturn')}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Instructions */}
                            <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>{t('reservationDetails.instructions.title')}</strong><br/>
                                {t('reservationDetails.instructions.renterRole')}<br/>
                                {t('reservationDetails.instructions.ownerRole')}<br/>
                                {t('reservationDetails.messages.technicalProblem')}
                              </Typography>
                            </Box>
                          </Card>
                        )}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Section Localisation */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">{t('reservationDetails.sections.location', 'Localisation')}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <Box sx={{ p: 3, bgcolor: '#fff' }}>
                          <Typography variant="body1" sx={{ 
                            lineHeight: 1.7, 
                            color: '#374151',
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            mb: 3
                          }}>
                            {listing.address}
                          </Typography>
                          
                          {/* Map Integration */}
                          <Box sx={{ mb: 3 }}>
                            {listing.offer_type === 'ride_share' ? (
                              <Box>
                                <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#1f2937' }}>
                                   🚗 {t('listing.address.rideShareMapTitle', 'Carte du trajet')}
                                </Typography>
                                <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                  <RideShareMap 
                                     startPosition={listing.latitude && listing.longitude ? [listing.latitude, listing.longitude] : null}
                                     endPosition={listing.booking_settings?.destinationLat && listing.booking_settings?.destinationLng ? [listing.booking_settings.destinationLat, listing.booking_settings.destinationLng] : null}
                                     readOnly={true}
                                     height={300}
                                  />
                                </Box>
                                {(listing.booking_settings?.destinationAddress) && (
                                   <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                     <Typography variant="body2" color="text.secondary">
                                       <strong>{t('listing.map.arrival', 'Arrivée')}:</strong> {listing.booking_settings.destinationAddress}
                                     </Typography>
                                   </Box>
                                )}
                              </Box>
                            ) : (
                                (listing.latitude && listing.longitude) && (
                                  <Box sx={{ height: 300, mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                    <ReadOnlyMap
                                      latitude={listing.latitude}
                                      longitude={listing.longitude}
                                      address={listing.address}
                                      height={300}
                                      zoom={15}
                                      showAddress={false}
                                    />
                                  </Box>
                                )
                            )}
                          </Box>
                    </Box>
                </AccordionDetails>
              </Accordion>

              {/* Section 3: Paiements */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">{t('reservationDetails.sections.payments')}</Typography>
                    <Chip 
                      {...getPaymentStatusConfig(reservation.payment_status)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                        <Typography variant="h6" fontWeight="600" color="success.main" sx={{ mb: 1 }}>
                          {formatCurrency(parseFloat(reservation.total_amount || 0), currency)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{t('reservationDetails.labels.totalAmountLower')}</Typography>
                        {reservation.payment_reference && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                            {t('reservationDetails.labels.paymentReference')}: {reservation.payment_reference}
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                    
                    {hasExtraFees && (
                      <Grid item xs={12} md={6}>
                        <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                          <Typography variant="h6" fontWeight="600" color="warning.main">{t('reservationDetails.messages.extraFees')}</Typography>
                          {Object.entries(reservation.extra_fees).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="caption">{key}</Typography>
                              <Typography variant="caption" fontWeight="500">
                                {formatCurrency(parseFloat(value), currency)}
                              </Typography>
                            </Box>
                          ))}
                        </Card>
                      </Grid>
                    )}

                    {/* Liste des paiements si disponible */}
                    {visiblePayments.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>{t('reservationDetails.messages.paymentsHistory')}</Typography>

                        <DisputeSummaryCard reservation={reservation} />

                        {/* Accordéon pour les Litiges */}
                        {(Array.isArray(reservation.disputes) && reservation.disputes.length > 0) || reservation.dispute ? (
                          <Box sx={{ mb: 2 }}>
                            {Array.from(Array.isArray(reservation.disputes) ? reservation.disputes : [reservation.dispute]).filter(Boolean).map((dispute, index) => (
                              <Accordion key={dispute?.id || index} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <GavelIcon sx={{ color: 'warning.main' }} />
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        {t('reservationDetails.sections.dispute', 'Litige')}
                                      </Typography>
                                      <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', mx: 1 }} />
                                      <Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                        {`#${String(dispute?.id || 'N/A')}`}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Chip 
                                        label={t(`reservationDetails.disputeStatus.${dispute?.status || 'unknown'}`, dispute?.status || 'unknown')} 
                                        size="small" 
                                        color={dispute?.status === 'resolved' ? 'success' : dispute?.status === 'escalated' ? 'warning' : 'default'}
                                        variant="outlined"
                                      />
                                      <Button
                                        size="small"
                                        endIcon={<OpenInNewIcon fontSize="small" />}
                                        href={`/disputes/${dispute?.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="text"
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {t('tooltips.viewDetails', 'Voir')}
                                      </Button>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ width: '100%' }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('reservationDetails.disputeField.status', 'Statut')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                          {t(`reservationDetails.disputeStatus.${dispute?.status || 'unknown'}`, dispute?.status || 'unknown')}
                                        </Typography>
                                      </Grid>
                                      {dispute?.compensation_amount && (
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.disputeField.compensationAmount', 'Montant de compensation')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {formatCurrency(parseFloat(dispute.compensation_amount), currency)}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {dispute?.compensation_type && (
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.disputeField.compensationType', 'Type de compensation')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {dispute.compensation_type}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {dispute?.created_at && (
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.disputeField.createdAt', 'Créé le')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {formatDate(dispute.created_at)}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {dispute?.updated_at && (
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.disputeField.updatedAt', 'Mis à jour le')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {formatDate(dispute.updated_at)}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {dispute?.resolved_at && (
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.disputeField.resolvedAt', 'Résolu le')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {formatDate(dispute.resolved_at)}
                                          </Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        ) : null}

                        {/* Accordéon pour les Pénalités */}
                        {Array.isArray(reservation.penalties) && reservation.penalties.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            {reservation.penalties.map(penalty => (
                              <Accordion key={penalty?.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <GavelIcon sx={{ color: 'error.main' }} />
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        {t('reservationDetails.sections.penalty', 'Pénalité')}
                                      </Typography>
                                      <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', mx: 1 }} />
                                      <Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                        {`#${String(penalty?.id)}`}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Chip 
                                        label={formatCurrency(penalty?.amount || 0, currency)} 
                                        size="small" 
                                        color="error"
                                      />
                                      <Chip 
                                        label={t(`reservationDetails.penaltyStatus.${penalty?.status || 'unknown'}`, penalty?.status || 'unknown')} 
                                        size="small" 
                                        variant="outlined"
                                      />
                                      <Button
                                        size="small"
                                        endIcon={<OpenInNewIcon fontSize="small" />}
                                        href={`/penalties/${penalty?.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="text"
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {t('tooltips.viewDetails', 'Voir')}
                                      </Button>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ width: '100%' }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('reservationDetails.penaltyField.status', 'Statut')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                          {t(`reservationDetails.penaltyStatus.${penalty?.status || 'unknown'}`, penalty?.status || 'unknown')}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('reservationDetails.penaltyField.amount', 'Montant')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                          {formatCurrency(parseFloat(penalty?.amount || 0), currency)}
                                        </Typography>
                                      </Grid>
                                      {penalty?.reason && (
                                        <Grid item xs={12}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.penaltyField.reason', 'Raison')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {penalty.reason}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {penalty?.created_at && (
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('reservationDetails.penaltyField.createdAt', 'Créé le')}
                                          </Typography>
                                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                                            {formatDate(penalty.created_at)}
                                          </Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        )}

                        <List dense>
                          {visiblePayments.map((payment, index) => {
                            const paymentType = payment.type || payment.payment_type;
                            const isDepositRow = paymentType === PAYMENT_TYPES.DEPOSIT;
                            const statusChipProps = isDepositRow
                              ? hasDepositAmount
                                ? getDepositStatusConfig(deposits[0].status)
                                : { color: 'default', label: t('reservationDetails.messages.depositNotRequired'), icon: <InfoIcon /> }
                              : getPaymentStatusConfig(payment.status);
                            return (
                            <ListItem key={index} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                              <ListItemIcon>
                                <CreditCardIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${formatCurrency(parseFloat(payment.amount), payment.currency || currency)} - ${getPaymentTypeLabel(paymentType)}`}
                                secondary={`${getPaymentMethodLabel(payment.method || payment.payment_method)} • ${formatDate(payment.transaction_date || payment.created_at)}`}
                              />
                              <Chip 
                                {...statusChipProps}
                                size="small"
                                variant="outlined"
                              />
                            </ListItem>
                            );
                          })}
                        </List>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Section 4: Caution/Dépôt */}
              {(reservation.deposit_amount != null || deposits.length > 0) && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon color="primary" />
                      <Typography variant="h6" fontWeight="600">{t('reservationDetails.sections.deposit')}</Typography>
                      {deposits.length > 0 && deposits[0]?.status && (
                        <Chip
                          {...getDepositStatusConfig(deposits[0].status)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {reservation.deposit_amount != null && (
                        <Grid item xs={12} md={6}>
                          <DisputeSummaryCard reservation={reservationDataForDisplay} />
                          <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                            <Typography variant="h6" fontWeight="600" color="info.main">
                              {hasDepositAmount ? formatCurrency(depositAmountValue, currency) : t('reservationDetails.messages.depositNotRequired')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{t('reservationDetails.labels.depositAmount')}</Typography>
                            {hasDepositAmount && deposits.length > 0 && deposits[0]?.status && (
                              <Chip 
                                {...getDepositStatusConfig(deposits[0].status)}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            )}                             
                          </Card>
                        </Grid>
                      )}

                      {/* Détails des dépôts */}
                      {deposits.length > 0 && hasDepositAmount && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>{t('reservationDetails.messages.depositsHistory')}</Typography>
                          <List dense>
                            {deposits.map((deposit, index) => (
                              <ListItem key={index} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                                <ListItemIcon>
                                  <AccountBalanceIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={`${formatCurrency(parseFloat(deposit.amount), currency)}`}
                                  secondary={`${t('reservationDetails.labels.createdOn')} ${formatDate(deposit.created_at)}`}
                                />
                                <Chip 
                                  {...getDepositStatusConfig(deposit.status)}
                                  size="small"
                                  variant="outlined"
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Section 5: Notes et commentaires */}
              {(reservation.notes || reservation.special_requests || reservation.renter_notes || reservation.owner_notes || reservation.damage_report) && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotesIcon color="primary" />
                      <Typography variant="h6" fontWeight="600">{t('reservationDetails.messages.notesComments')}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {reservation.special_requests && (
                        <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                          <Typography variant="subtitle2" fontWeight="600" color="info.main" gutterBottom>
                            {t('reservationDetails.messages.specialRequests')}
                          </Typography>
                          <Typography variant="body2">{reservation.special_requests}</Typography>
                        </Card>
                      )}

                      {reservation.notes && (
                        <Card elevation={1} sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight="600" gutterBottom>{t('reservationDetails.messages.generalNotes')}</Typography>
                          <Typography variant="body2">{reservation.notes}</Typography>
                        </Card>
                      )}

                      {reservation.renter_notes && (
                        <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Typography variant="subtitle2" fontWeight="600" color="primary.main" gutterBottom>
                            {t('reservationDetails.messages.renterNotes')}
                          </Typography>
                          <Typography variant="body2">{reservation.renter_notes}</Typography>
                        </Card>
                      )}

                      {reservation.owner_notes && (
                        <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                          <Typography variant="subtitle2" fontWeight="600" color="secondary.main" gutterBottom>
                            {t('reservationDetails.messages.ownerNotes')}
                          </Typography>
                          <Typography variant="body2">{reservation.owner_notes}</Typography>
                        </Card>
                      )}

                      {reservation.damage_report && (
                        <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
                          <Typography variant="subtitle2" fontWeight="600" color="error.main" gutterBottom>
                            {t('reservationDetails.messages.damageReport')}
                          </Typography>
                          <Typography variant="body2">{reservation.damage_report}</Typography>
                          {hasDamages && (
                            <Typography variant="h6" fontWeight="600" color="error.main" sx={{ mt: 1 }}>
                              {t('reservationDetails.messages.damageCost')} {formatCurrency(parseFloat(reservation.damage_amount), currency)}
                            </Typography>
                          )}
                        </Card>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Section 6: Évaluations bidirectionnelles */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RateReviewIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">{t('reservationDetails.sections.evaluations')}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={3}>
                    {/* Review owner→renter */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                        {t('reservationDetails.reviews.ownerEvaluation')}
                      </Typography>
                      {reviewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : (
                        <OwnerCreatedReview reservation={reservation} />
                      )}
                    </Box>

                    {/* Review renter→owner */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                        {t('reservationDetails.reviews.renterEvaluation')}
                      </Typography>
                      {reviewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : reservationReview ? (
                        <OwnerReviewDisplay reservation={reservationDataForDisplay} userRole={userRole} />
                      ) : (
                        <OwnerReviewEmpty />
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Section 7: Informations d'annulation */}
              {reservation.status === RESERVATION_STATUS.CANCELLED && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GavelIcon color="error" />
                      <Typography variant="h6" fontWeight="600" color="error.main">
                        {t('reservationDetails.sections.cancellationInfo')}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Card elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" fontWeight="600" color="error.main" gutterBottom>
                            {t('reservationDetails.messages.cancellationDate')}
                          </Typography>
                          <Typography variant="body2">
                            {reservation.cancelled_at ? formatDate(reservation.cancelled_at) : 'Non spécifiée'}
                          </Typography>
                        </Grid>
                        
                        {reservation.cancelled_by && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight="600" color="error.main" gutterBottom>
                              {t('reservationDetails.messages.cancelledBy')}
                            </Typography>
                            <Typography variant="body2">
                              {(() => {
                                // Comparer cancelled_by avec renter_id ou renter?.id
                                const renterId = reservation.renter_id || reservation.renter?.id;
                                if (reservation.cancelled_by === renterId) {
                                  return t('reservationDetails.sections.participant.renter');
                                } else {
                                  return t('reservationDetails.sections.participant.owner');
                                }
                              })()}
                            </Typography>
                          </Grid>
                        )}
                        
                        {reservation.cancellation_reason && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight="600" color="error.main" gutterBottom>
                              {t('reservationDetails.messages.cancellationReason')}
                            </Typography>
                            <Typography variant="body2">{reservation.cancellation_reason}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Section 8: Rapports de condition */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">
                      {t('reservationDetails.messages.conditionReports')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <ConditionReportsSection reservationId={reservation.id} />
                </AccordionDetails>
              </Accordion>

              {/* Section 9: Documents */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" fontWeight="600">
                      {t('reservationDetails.sections.documents')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Card elevation={1} sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('reservationDetails.messages.documentsDescription')}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadContract}
                        fullWidth
                        sx={{
                          py: 1.5,
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                          }
                        }}
                      >
                        {t('reservationDetails.actions.downloadContract')}
                      </Button>
                    </Stack>
                  </Card>
                </AccordionDetails>
              </Accordion>

              {/* Informations sur l'autre participant */}
              {otherParticipant && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6" fontWeight="600">
                        {userRole === 'renter' ? t('reservationDetails.sections.participant.owner') : t('reservationDetails.sections.participant.renter')}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Card elevation={1} sx={{ p: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          src={otherParticipant.avatar}
                          sx={{ width: 60, height: 60 }}
                        >
                          {otherParticipant.firstName?.[0] || otherParticipant.first_name?.[0] || <PersonIcon />}
                        </Avatar>
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="600">
                            {otherParticipant.firstName || otherParticipant.first_name} {otherParticipant.lastName || otherParticipant.last_name}
                          </Typography>
                          
                          {otherParticipant.email && (
                            <Typography variant="body2" color="text.secondary">
                              {otherParticipant.email}
                            </Typography>
                          )}
                          
                          {otherParticipant.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {otherParticipant.phone}
                            </Typography>
                          )}
                          
                          {/* Rating si disponible */}
                          {otherParticipant.rating && (
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                              <StarIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                              <Typography variant="body2" fontWeight="500">
                                {otherParticipant.rating}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ({otherParticipant.reviewsCount || 0} avis)
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Message informatif quand la messagerie est désactivée */}
              {(reservation.status === RESERVATION_STATUS.CANCELLED || 
                reservation.status === RESERVATION_STATUS.EXPIRED) && (
                <Card 
                  elevation={0} 
                  sx={{ 
                    mt: 3,
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: alpha(theme.palette.info.main, 0.05)
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main'
                      }}>
                        <CancelIcon sx={{ fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="600" color="info.main" gutterBottom>
                          {t('reservationDetails.messages.messagingDisabled')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reservation.status === RESERVATION_STATUS.CANCELLED 
                            ? t('reservationDetails.messages.messagingNotAvailableCancelled')
                            : t('reservationDetails.messages.messagingNotAvailableExpired')
                          }
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

        {/* Panneau droit - Messagerie intégrée (seulement si réservation active) */}
        {(reservation.status !== RESERVATION_STATUS.CANCELLED && 
          reservation.status !== RESERVATION_STATUS.EXPIRED) && (
          <Box sx={{ 
            backgroundColor: '#f8fafc',
            borderLeft: '1px solid rgba(0,0,0,0.06)',
            position: 'relative',
            display: { xs: mobileTab === 1 ? 'flex' : 'none', md: 'flex' },
            flex: { xs: 1, md: 'initial' },
            height: { xs: '100%', md: 'auto' },
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Contenu de la messagerie sans éléments parasites */}
            <Box sx={{ 
              flex: 1,
              overflow: 'hidden'
            }}>
              <MessagingInterface
                reservationId={reservation.id}
                reservation={reservation}
                currentUser={currentUser}
                onClose={() => {}} // Pas de fermeture dans cette vue intégrée
                embedded={true} // Mode intégré sans en-tête
                unreadCount={getUnreadCount(reservation.id)}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Modales QR Code */}
      {qrGeneratorOpen && (
        <QRCodeGeneratorModal
          open={qrGeneratorOpen}
          onClose={() => setQrGeneratorOpen(false)}
          reservation={reservation}
          type={qrType}
          onGenerate={handleGenerateQR}
          loading={qrLoading}
        />
      )}

      {qrScannerOpen && (
        <QRCodeConfirmationModal
          open={qrScannerOpen}
          onClose={() => setQrScannerOpen(false)}
          reservation={reservation}
          type={qrType}
          expectedCode={qrType === 'pickup' ? reservation.client_code : reservation.owner_code}
          qrType={qrType}
          userRole={userRole}
          onConfirm={handleConfirmation}
          onRefuse={(refusalData) => {
            console.log('ReservationDetailsPage - Refus reçu:', refusalData);
            toast.info('Fonctionnalité de refus non implémentée dans cette page');
          }}
          loading={qrLoading}
        />
      )}

      {/* Dispute Creation Dialog */}
      <Dialog 
        open={disputeDialogOpen} 
        onClose={() => setDisputeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <CreateDisputeForm 
            reservationId={reservation.id}
            onSuccess={() => {
              setDisputeDialogOpen(false);
              toast.success(t('disputes.create.success', 'Litige signalé avec succès'));
              if (onDataUpdate) onDataUpdate();
            }}
            onCancel={() => setDisputeDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Fonction de comparaison optimisée pour éviter les re-renders inutiles
const arePropsEqual = (prevProps, nextProps) => {
  // Comparer les propriétés critiques
  if (prevProps.reservation?.id !== nextProps.reservation?.id) return false;
  if (prevProps.reservation?.status !== nextProps.reservation?.status) return false;
  if (prevProps.reservation?.pickup_confirmed !== nextProps.reservation?.pickup_confirmed) return false;
  if (prevProps.reservation?.return_confirmed !== nextProps.reservation?.return_confirmed) return false;
  if (prevProps.currentUser?.id !== nextProps.currentUser?.id) return false;
  if (prevProps.userRole !== nextProps.userRole) return false;
  
  // Ignorer les changements de callbacks pour éviter les re-renders inutiles
  return true;
};

export default React.memo(ReservationDetailsPage, arePropsEqual);
